import { simulationSiteId } from './lib/sim/projects/simulationSite';
import { savedWorkspaceUiState } from "./lib/savedWorkspaceUiState";
import React from "react";
import { retryImport } from "./components/siterag/docViewers/retryImport";
import { RouteLoadingSkeleton } from "./components/ui/RouteLoadingSkeleton";
import { ChunkLoadBoundary, requireLoadedModule } from "./components/ChunkLoadBoundary";
import { useRecordUserSession } from "./lib/useRecordUserSession";
import { useLocation, useNavigate } from "react-router-dom";
// The simulation editor workspace (runtime, header controls, tab bar, canvas)
// and its File menu actions are code-split: the dashboard and site pages never
// download or run them. App mounts the workspace on the first visit to the
// simulation view and keeps it mounted afterwards. The File menu items come
// from the same chunk: the menu shows them only in the simulation view, where
// that chunk has already loaded, so the first open never waits on a fetch.
const EditorWorkspace = React.lazy(
  retryImport(() =>
    import("./components/editor/EditorWorkspace").then((m) =>
      requireLoadedModule(m, "EditorWorkspace"),
    ),
  ),
);
const EditorFileMenuItems = React.lazy(
  retryImport(() =>
    import("./components/editor/EditorWorkspace").then((m) => ({
      default: requireLoadedModule(m, "EditorWorkspace").EditorFileMenuItems,
    })),
  ),
);
import { CoBrandLockup } from "./components/auth/CoBrandLockup";
import { SimulationWorkspaceNavigation } from "./components/editor/SimulationWorkspaceNavigation";
import { Toaster, toast } from "sonner";
import { Menu, Trash2, Palette, MapPin, LayoutDashboard, BarChart3, Shield, Truck, Lock, Unlock, ArrowLeft } from "lucide-react";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { useStore } from "./store";
import { useShallow } from "zustand/react/shallow";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSeparator,
} from "./components/ui/dropdown-menu";
// The cloud storage dialog is opened only from the simulation editor; load it
// on first open and keep it mounted afterwards so its state survives closing.
const SaveLoadDialog = React.lazy(
  retryImport(() =>
    import("./components/SaveLoadDialog").then((m) => ({
      default: requireLoadedModule(m, "SaveLoadDialog").SaveLoadDialog,
    })),
  ),
);
import { UserApiKeysSettings } from "./components/UserApiKeysSettings";
import { LandGateConnectionDialog } from "./components/landgate/LandGateConnectionDialog";
import { UploadQueuePanel } from "./components/UploadQueuePanel";
// Admin-only surface; code-split and mounted only on the /admin/* routes.
const AdminPanel = React.lazy(
  retryImport(() =>
    import("./components/admin/AdminPanel").then((m) => ({ default: m.AdminPanel })),
  ),
);
import {
  isAdminSidebarKey,
  type AdminSidebarKey,
} from "./components/admin/AdminSidebar";
// AutoSaver persists canvas tabs and view state; it carries simulation
// serialisers, so it loads as its own chunk right after the shell renders.
// Every session mounts it, so a failed fetch is contained by a
// ChunkLoadBoundary instead of reaching the root error page.
const AutoSaver = React.lazy(
  retryImport(() =>
    import("./components/AutoSaver").then((m) => ({
      default: requireLoadedModule(m, "AutoSaver").AutoSaver,
    })),
  ),
);
import { useToastVisibility } from "./lib/useToastVisibility";
import { isEoEngineActive } from "./lib/sim/eo/eoEngineFlag";
import { DmsMetricsAutoFlusher } from "./components/DmsMetricsAutoFlusher";
import { SiteRagEngine } from "./components/siterag/SiteRagEngine";
// #81(c) — first-login welcome overlay (self-gating on a per-uid
// localStorage flag; shows once even when the user already has sites).
import type { EngineView } from "./components/siterag/useUrlSiteHydration";
import { UserMenu } from "./components/UserMenu";
import {
  resolveEffectiveLogo,
  DEFAULT_BRAND_LOGO_URL,
} from "./lib/resolveEffectiveLogo";
// Wave B4 (#30) — company-role permission table gates the Company Suppliers
// menu entry: supplier shortlisting/editing is a `canCreate` capability, so
// viewers (canCreate=false) never see the affordance and never hit a silent
// permission-denied toast.
import { ROLE_PERMISSIONS } from "./types/company";
import { NotificationBell } from "./components/NotificationBell";
import { ThemeToggle } from "./components/ThemeToggle";
import { FeedbackButton } from "./components/feedback/FeedbackButton";
import { ImpersonationBanner } from "./components/common/ImpersonationBanner";
import { CONFIG } from "./config/ui";
import { auth } from "./firebase";
// R-131-SIM-CANVAS-G — `import * as LucideIcons` was only used by the
// legacy custom-component icon lookup; ComponentLibraryDrawer owns it now.
const MyIssuesPage = React.lazy(
  retryImport(() => import("./pages/MyIssuesPage").then((m) => ({ default: m.MyIssuesPage }))),
);
const IssueStatusPage = React.lazy(
  retryImport(() =>
    import("./pages/IssueStatusPage").then((m) => ({ default: m.IssueStatusPage })),
  ),
);
import {
  adminLegacyRedirect,
  isRootDashboardEntryPath,
  isSiteScopedPath,
  parseSiteAppPath,
  siteOverviewPath,
} from "./lib/siteNavigation";
import { cn } from "@/lib/utils";
import { OPEN_LANDGATE_CONNECTION_EVENT } from "./services/landgate/landGateConnectionClient";

/**
 * RUN-CONSOLIDATION (toolbar-consolidation) — locale-tolerant numeric parse for
 * the Simulation Settings inputs.
 *
 * The previous handlers used `parseFloat(e.target.value)`, which reads the raw
 * string. In comma-decimal locales (de/fr/…), the browser surfaces the value as
 * e.g. "0,1" / "0,8" / "0,001"; `parseFloat` stops at the comma and yields 0,
 * silently zeroing the user's relaxation / tolerance / time-step. We prefer the
 * input's already-locale-parsed `valueAsNumber` (a real number for "0,1"), and
 * fall back to a comma→dot `parseFloat` for safety. A non-finite result (empty
 * field) returns `fallback` so the existing defaults are preserved.
 */
export function parseLocaleNumber(
  input: HTMLInputElement,
  fallback: number,
): number {
  const viaInput = input.valueAsNumber;
  if (Number.isFinite(viaInput)) return viaInput;
  const viaString = parseFloat(String(input.value).replace(",", "."));
  return Number.isFinite(viaString) ? viaString : fallback;
}

// Re-exported for existing importers; the helper lives in a tiny module the
// lazily loaded editor workspace shares.
export { isSteadyStateExecutionMode } from "./lib/sim/steadyStateMode";

// Aurora Cockpit — the top segmented nav (DASHBOARD · GUIDE · SITE · REPORT)
// adopts the round2 command-rail `.cr-seg__tab` token language: a single inset
// track, mono-uppercase tabs, and ONE consistent active pill (surface-2 fill +
// a primary-tinted hairline and the cockpit glow). Icons live inside each tab
// so the active treatment is identical across all four.
function siteragNavSegmentClass(active: boolean): string {
  return cn(
    "inline-flex h-7 items-center justify-center gap-1.5 shrink-0 rounded-md px-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] transition-colors border",
    active
      ? "border-primary/55 bg-surface-2 text-text-primary shadow-[0_0_0_1px_color-mix(in_srgb,var(--primary)_12%,transparent),0_0_14px_-6px_color-mix(in_srgb,var(--primary)_60%,transparent)]"
      : "border-transparent text-text-muted hover:bg-state-hover/50 hover:text-text-secondary",
  );
}

export default function App() {
  const simulationComputeEnabled = isEoEngineActive();
  const [isCloudDialogOpen, setIsCloudDialogOpen] = React.useState(false);
  const [isApiKeysSettingsOpen, setIsApiKeysSettingsOpen] =
    React.useState(false);
  const [isLandGateConnectionOpen, setIsLandGateConnectionOpen] =
    React.useState(false);
  const [isUploadQueueOpen, setIsUploadQueueOpen] = React.useState(false);
  // ── Routing — AP-routing pack ──────────────────────────────────────
  // The admin panel is now URL-driven (`/admin`, `/admin/:section`,
  // `/admin/:section/:subsection`). The legacy state flag is gone —
  // `isAdminPanelOpen` is derived from `useLocation()`. `initialTab` is the
  // first path segment after `/admin/`; AdminPanel resolves it the same way
  // it used to handle the prop. We keep the union type narrow so callers of
  // the legacy `open-admin-panel` CustomEvent still get type checking.
  const location = useLocation();
  const navigate = useNavigate();
  // The authenticated application is a viewport workbench whose individual
  // rails own their scrolling. Letting Chromium scroll `html`/`body` while it
  // focuses a lower inspector input moves the entire Sim Canvas and can leave
  // otherwise-valid React Flow handles outside the pointer viewport. Contain
  // document scrolling for the lifetime of this shell and restore the host
  // styles if the app is unmounted (for example, when navigating to a separate
  // public surface in an embedding host).
  React.useLayoutEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previous = {
      htmlOverflow: html.style.overflow,
      htmlOverscrollBehavior: html.style.overscrollBehavior,
      bodyOverflow: body.style.overflow,
      bodyOverscrollBehavior: body.style.overscrollBehavior,
    };
    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    window.scrollTo(0, 0);
    return () => {
      html.style.overflow = previous.htmlOverflow;
      html.style.overscrollBehavior = previous.htmlOverscrollBehavior;
      body.style.overflow = previous.bodyOverflow;
      body.style.overscrollBehavior = previous.bodyOverscrollBehavior;
    };
  }, []);
  const isAdminPanelOpen = location.pathname.startsWith("/admin");
  type AdminInitialTab = AdminSidebarKey;
  const adminPanelInitialTab: AdminInitialTab | undefined =
    React.useMemo(() => {
      if (!isAdminPanelOpen) return undefined;
      const segment = location.pathname
        .replace(/^\/admin\/?/, "")
        .split("/")[0];
      if (!segment) return "now";
      if (
        segment === "users" ||
        segment === "companies" ||
        segment === "members"
      )
        return "identity";
      return isAdminSidebarKey(segment) ? segment : "now";
    }, [isAdminPanelOpen, location.pathname]);

  // Listen for cross-component requests to open the AdminPanel on a specific
  // tab. Used by the report Provider-failure CTA to land admins on Health
  // when a config-type provider failure needs an env-var fix, and by the
  // report lineage chip (Datacrate Model Prompt 13) to land on the DAM tab.
  //
  // AP-routing migration: instead of flipping a local state flag we now push
  // the matching `/admin/<tab>` URL onto the router. The DamLineageChip /
  // ProviderFailureCard call-sites are unchanged — they keep dispatching the
  // CustomEvent and we translate it to navigation here. Tests in those files
  // continue to assert the event fires; this handler is the bridge from the
  // event bus to the router for any callers we haven't migrated yet.
  React.useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ tab?: AdminInitialTab }>).detail;
      const tab = detail?.tab;
      navigate(tab ? `/admin/${tab}` : "/admin");
    };
    window.addEventListener("open-admin-panel", handler);
    return () => window.removeEventListener("open-admin-panel", handler);
  }, [navigate]);
  React.useEffect(() => {
    const handler = () => setIsUploadQueueOpen(true);
    window.addEventListener("open-upload-queue-panel", handler);
    return () => window.removeEventListener("open-upload-queue-panel", handler);
  }, []);

  // Vite emits `vite:preloadError` when a dynamically-imported module
  // (a content-hashed lazy chunk — every doc viewer, every code-split
  // route) 404s. The dominant cause: the user's tab is older than the
  // current deploy, so the chunk hash it asks for no longer exists on
  // the server. Without this handler the browser surfaces a bare
  // "Failed to fetch dynamically imported module" with no recovery path
  // (DocViewerDialog's Suspense boundary cannot catch a rejected
  // import()). Catch it at the root, preventDefault so we own the UX,
  // and show a sticky toast prompting a refresh. We also auto-reload on
  // the next visibility change (e.g. the user tabs back) as a safety
  // net, and after a short grace period if the tab stays focused.
  React.useEffect(() => {
    let reloadTimer: ReturnType<typeof setTimeout> | null = null;
    let triggered = false;
    const reloadNow = () => {
      if (triggered) return;
      triggered = true;
      // Use `window.location` explicitly — `location` is the React Router
      // hook result in this scope (AP-routing import), which has no .reload().
      window.location.reload();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") reloadNow();
    };
    const onPreloadError = (e: Event) => {
      // Stop the default window-level error event so the generic
      // browser overlay / unhandledrejection path doesn't also fire.
      e.preventDefault();
      toast.error(
        "A newer version of Datacrate is available. Refresh to continue.",
        {
          id: "vite-preload-error", // dedupe — only one banner regardless of how many chunks fail
          duration: Infinity,
          action: {
            label: "Refresh",
            onClick: reloadNow,
          },
        },
      );
      // Auto-recover even if the user ignores the banner: reload when
      // they next focus the tab, or after a 30s grace period.
      document.addEventListener("visibilitychange", onVisible);
      if (reloadTimer) clearTimeout(reloadTimer);
      reloadTimer = setTimeout(reloadNow, 30_000);
    };
    window.addEventListener("vite:preloadError", onPreloadError);
    return () => {
      window.removeEventListener("vite:preloadError", onPreloadError);
      document.removeEventListener("visibilitychange", onVisible);
      if (reloadTimer) clearTimeout(reloadTimer);
    };
  }, []);

  // Every view records the signed-in session; it must not wait for the
  // code-split editor workspace (see useRecordUserSession).
  useRecordUserSession();
  const [siteRagView, setSiteRagView] = React.useState<EngineView>("dashboard");
  const useDashboardV2 = React.useMemo(
    () =>
      CONFIG.UI.DASHBOARD_V2 &&
      !location.pathname.startsWith("/dashboard-legacy"),
    [location.pathname],
  );
  const isIssuesListRoute = location.pathname === "/issues";
  const isIssueStatusRoute = /^\/issues\/[^/]+$/.test(location.pathname);
  // R-131-SIM-CANVAS-G — palette expand state moved into ComponentLibraryDrawer.
  // PERF (2026-10-07) — this used to be a bare `useStore()`, which subscribes
  // the whole app shell to EVERY store write (each 30s job heartbeat, every
  // report/evidence/progress tick) and re-rendered the entire tree beneath it.
  // Select exactly the fields the shell reads; `useShallow` keeps the picked
  // object stable while none of them changed.
  const {
    simState,
    nodes,
    edges,
    loadCycle,
    updateSimState,
    clearSimulation,
    resetSimulation,
    tabs,
    activeTabId,
    setActiveTab,
    closeTab,
    addTab,
    updateTabName,
    customComponents,
    deleteCustomComponent,
    viewMode,
    setViewMode,
    setSite,
    site,
    requestShowAnalysis,
    runRequestToken,
    companyId,
    userRole,
    // #110 — true while a sonner toast is visible (hides the header wordmark).
    toastVisible,
    // SIM-MGMT (Wire phase) — saved-cloud-sim management + view-state restore.
    deleteCloudSim,
    renameCloudSim,
    listSimBackups,
    restoreSimBackup,
    setCompactView,
    setColorOverlay,
    setCanvasBackgroundMode,
    setCanvasMetersPerFlowUnit,
    // #115 — canvas-panel + guide overlay visibility setters (restored on load).
    setShowMinimap,
    setShowControls,
    setShowSolverStatus,
    setShowInspector,
    setShowGuide,
    compactView,
    colorOverlay,
    canvasBackgroundMode,
    canvasMetersPerFlowUnit,
    showMinimap,
    showControls,
    showSolverStatus,
    showInspector,
    showGuide,
    flowViewport,
    // #116 — Layers/Cycles: components marked "ignore" are excluded from the solve.
    ignoredNodeIds,
  } = useStore(useShallow((s) => ({
    simState: s.simState,
    nodes: s.nodes,
    edges: s.edges,
    loadCycle: s.loadCycle,
    updateSimState: s.updateSimState,
    clearSimulation: s.clearSimulation,
    resetSimulation: s.resetSimulation,
    tabs: s.tabs,
    activeTabId: s.activeTabId,
    setActiveTab: s.setActiveTab,
    closeTab: s.closeTab,
    addTab: s.addTab,
    updateTabName: s.updateTabName,
    customComponents: s.customComponents,
    deleteCustomComponent: s.deleteCustomComponent,
    viewMode: s.viewMode,
    setViewMode: s.setViewMode,
    setSite: s.setSite,
    site: s.site,
    requestShowAnalysis: s.requestShowAnalysis,
    runRequestToken: s.runRequestToken,
    companyId: s.companyId,
    userRole: s.userRole,
    toastVisible: s.toastVisible,
    deleteCloudSim: s.deleteCloudSim,
    renameCloudSim: s.renameCloudSim,
    listSimBackups: s.listSimBackups,
    restoreSimBackup: s.restoreSimBackup,
    setCompactView: s.setCompactView,
    setColorOverlay: s.setColorOverlay,
    setCanvasBackgroundMode: s.setCanvasBackgroundMode,
    setCanvasMetersPerFlowUnit: s.setCanvasMetersPerFlowUnit,
    setShowMinimap: s.setShowMinimap,
    setShowControls: s.setShowControls,
    setShowSolverStatus: s.setShowSolverStatus,
    setShowInspector: s.setShowInspector,
    setShowGuide: s.setShowGuide,
    compactView: s.compactView,
    colorOverlay: s.colorOverlay,
    canvasBackgroundMode: s.canvasBackgroundMode,
    canvasMetersPerFlowUnit: s.canvasMetersPerFlowUnit,
    showMinimap: s.showMinimap,
    showControls: s.showControls,
    showSolverStatus: s.showSolverStatus,
    showInspector: s.showInspector,
    showGuide: s.showGuide,
    flowViewport: s.flowViewport,
    ignoredNodeIds: s.ignoredNodeIds,
  })));
  // The shell reads only the active site id + the site list from `siteRag`
  // (menus, lock label, routing) — never the slice itself, which is rebuilt on
  // every report / progress write.
  const siteRag = useStore(useShallow((s) => ({
    activeSiteId: s.siteRag.activeSiteId,
    sites: s.siteRag.sites,
  })));
  // `/editor` is a stable deep link, including for a newly authenticated
  // engineer whose last persisted shell view was the site dashboard. Enter
  // the existing simulation surface from URL intent alone; callers that
  // already set the mode remain idempotent and no canvas state is replaced.
  React.useEffect(() => {
    if (!simulationComputeEnabled) {
      if (viewMode === "simulation") {
        setViewMode("siterag");
        if (siteRagView !== "dashboard") setSiteRagView("dashboard");
      }
      return;
    }
    // React Router applies navigations in a transition, so while the user is
    // leaving /editor (e.g. the Dashboard tab) its `location` still reads
    // /editor after the address bar has moved on. Re-entering simulation from
    // that stale location fought the dashboard URL-sync effect, which reads
    // the address bar: the view flipped simulation/siterag until React gave up
    // with "Maximum update depth exceeded". Require both to agree.
    const browserPath =
      typeof window !== "undefined" ? window.location.pathname : location.pathname;
    if (
      location.pathname === "/editor" &&
      browserPath === "/editor" &&
      viewMode !== "simulation"
    ) {
      setViewMode("simulation");
    }
  }, [
    location.pathname,
    setViewMode,
    simulationComputeEnabled,
    siteRagView,
    viewMode,
  ]);
  // #110 — keep `toastVisible` in sync with the live sonner toast DOM so the
  // centered header wordmark hides while a toast is showing and restores after.
  useToastVisibility();
  // Brand logo (feat/org-logo) — the top-menu-center wordmark reflects the
  // EFFECTIVE logo (user override → org logo → Datacrate default), resolved
  // through the SAME helper the report cover branding uses. Subscribe to the
  // two roots and derive in a memo (per the Zustand-selector-stability rule —
  // never build a derived value inside the selector).
  const userLogoUrl = useStore((s) => s.userLogoUrl);
  const orgLogoUrl = useStore((s) => s.orgLogoUrl);
  // Wave B4 (#30) — company role drives the Company Suppliers menu gate.
  // Subscribe to the root and derive the permission flag in a memo (per the
  // Zustand-selector-stability rule — never build a derived value inside the
  // selector). `null` role → no company → treated as no supplier-edit access.
  const companyRole = useStore((s) => s.companyRole);
  const canEditSuppliers = React.useMemo(
    () => (companyRole ? ROLE_PERMISSIONS[companyRole].canCreate : false),
    [companyRole],
  );
  const headerLogoUrl = React.useMemo(
    () => resolveEffectiveLogo({ userLogoUrl, orgLogoUrl }),
    [userLogoUrl, orgLogoUrl],
  );
  const headerLogoIsCustom = headerLogoUrl !== DEFAULT_BRAND_LOGO_URL;

  // Site edit-lock (#82) — derive the active site's lock state for the FILE
  // menu Lock/Unlock label. `siteRag` is already a root subscription above, so
  // deriving here (not inside a selector) keeps the project's Zustand rule.
  // The menu only dispatches `datacrate:toggle-site-lock`; SiteLandingPage's
  // listener owns the lock/unlock decision and permission checks.
  const activeSiteLocked = React.useMemo<boolean>(() => {
    const activeId = siteRag.activeSiteId;
    if (!activeId) return false;
    const s = siteRag.sites.find((x) => x.id === activeId);
    return s?.locked === true;
  }, [siteRag.activeSiteId, siteRag.sites]);
  React.useEffect(() => {
    const handler = () => {
      if (userRole !== "admin") return; // R-130-2
      setIsApiKeysSettingsOpen(true); // R-130-2
    };
    window.addEventListener("open-api-keys-settings", handler);
    return () => window.removeEventListener("open-api-keys-settings", handler);
  }, [userRole]); // R-130-2
  React.useEffect(() => {
    const handler = () => {
      if (!auth.currentUser) {
        toast.error("Sign in to manage your LandGate connection.");
        return;
      }
      setIsLandGateConnectionOpen(true);
    };
    window.addEventListener(OPEN_LANDGATE_CONNECTION_EVENT, handler);
    return () =>
      window.removeEventListener(OPEN_LANDGATE_CONNECTION_EVENT, handler);
  }, []);
  React.useEffect(() => {
    const legacy = adminLegacyRedirect(location.pathname);
    if (legacy) {
      navigate(legacy, { replace: true });
      return;
    }
    if (location.pathname === "/compare") {
      navigate("/", { replace: true });
    }
  }, [location.pathname, navigate]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("legacySimulation") !== "retired") return;
    toast.warning("Legacy simulation reference retired", {
      description:
        "The old run was not fetched or remapped. Review this canvas and solve it to create new evidence.",
    });
    navigate("/editor", { replace: true });
  }, [location.search, navigate]);

  React.useEffect(() => {
    const browserPath =
      typeof window !== "undefined"
        ? window.location.pathname
        : location.pathname;
    const onDashboardRoute =
      isRootDashboardEntryPath(browserPath) ||
      isRootDashboardEntryPath(location.pathname) ||
      location.pathname.startsWith("/dashboard-legacy");
    if (!onDashboardRoute) return;
    // Sidebar picks update the address bar via replaceState before React Router
    // sees `/sites/{id}` — do not snap back to dashboard while a site is selected.
    if (siteRag.activeSiteId || isSiteScopedPath(browserPath)) return;
    if (viewMode !== "siterag") setViewMode("siterag");
    if (siteRagView !== "dashboard") setSiteRagView("dashboard");
  }, [
    location.pathname,
    setViewMode,
    siteRag.activeSiteId,
    siteRagView,
    viewMode,
  ]);

  // P3 — report-route resolver. Parses `/sites/:id/reports/:id` (and the
  // `/rerun` sub-route) off the PATHNAME only and flips store view-mode,
  // never the URL. This is deliberately hash-preserving: the audit-panel
  // RESOLUTION-LOOP deep-link lands on `/sites/<s>/reports/<r>#section-<id>`,
  // and the `#section-<id>` fragment is the scroll/highlight target that
  // SiteRagReportView's deep-link receiver reads from `window.location.hash`.
  // `parseSiteAppPath` operates on `window.location.pathname` (the fragment
  // is not part of pathname) and this effect issues NO `navigate()` /
  // `history.*` write, so the hash survives intact for the report view to
  // consume. Do NOT add a navigate()/replaceState here that rewrites the
  // URL without re-appending `window.location.hash` — that would strip the
  // deep-link before the report mounts.
  React.useEffect(() => {
    const browserPath =
      typeof window !== "undefined"
        ? window.location.pathname
        : location.pathname;
    const route = parseSiteAppPath(browserPath);
    if (!route.siteId) return;
    if (viewMode !== "siterag") setViewMode("siterag");
    if (route.reportId && !route.isRerun) {
      if (siteRagView !== "report") setSiteRagView("report");
      return;
    }
    if (siteRagView === "dashboard") setSiteRagView("site");
  }, [location.pathname, setViewMode, siteRagView, viewMode]);

  // Retired local playback flags can be present in old saved tabs. Clear them immediately; active
  // steady and transient execution is owned by the EO request hooks above.
  React.useEffect(() => {
    if (simState.isRunning) updateSimState({ isRunning: false });
  }, [simState.isRunning, updateSimState]);

  React.useEffect(() => {
    // Defer localStorage restore to next frame so the initial DOM render completes
    // before state changes trigger component re-mounting. This prevents Base UI
    // floating-ui dismiss handlers from firing on unmounted elements (getRootNode crash).
    requestAnimationFrame(() => {
      const saved = localStorage.getItem("savedSimulationTabs");
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          const path = window.location.pathname;
          const isRootEntryPath =
            path === "/" || path === "" || /^\/sites\/?$/.test(path);
          const parsedSitePath = parseSiteAppPath(path);

          if (parsed.siteRag) {
            // R-109c: first-load default should be Dashboard for root-style
            // entries, even if the previous session persisted a site/report.
            // REDHEAD-URL-1: deep-link paths (/sites/<siteId>...) remain
            // authoritative over stale localStorage activeSiteId/activeReportId.
            const nextSiteRag = isRootEntryPath
              ? { ...parsed.siteRag, activeSiteId: null, activeReportId: null }
              : parsedSitePath.siteId
                ? {
                    ...parsed.siteRag,
                    activeSiteId: parsedSitePath.siteId,
                    activeReportId: parsedSitePath.reportId ?? null,
                  }
                : parsed.siteRag;
            useStore.setState((state) => ({
              siteRag: { ...state.siteRag, ...savedWorkspaceUiState(nextSiteRag) },
            }));
          }

          // The personal simulation workspace restore is loaded on demand so
          // the shell bundle does not carry the canvas migrations. A
          // project/model deep link stays authoritative for the canvas: the
          // helper re-checks the path, and the restore is skipped if anything
          // replaced the canvas while the helper loaded.
          if (simulationComputeEnabled && parsed.tabs && parsed.activeTabId) {
            const before = useStore.getState();
            const tabIdsAtRequest = before.tabs.map((tab) => tab.id).join("|");
            void import("./store/restorePersonalWorkspace")
              .then(({ restorePersonalWorkspace }) => {
                const now = useStore.getState();
                if (
                  window.location.pathname !== path ||
                  now.tabs !== before.tabs ||
                  now.activeTabId !== before.activeTabId ||
                  now.nodes !== before.nodes ||
                  now.tabs.map((tab) => tab.id).join("|") !== tabIdsAtRequest
                ) {
                  return;
                }
                restorePersonalWorkspace(parsed, path);
              })
              .catch((error) => {
                console.error("Failed to load saved simulation from localStorage", error);
              });
          }
        } catch (e) {
          console.error("Failed to load saved simulation from localStorage", e);
        }
      }
    });
  }, [simulationComputeEnabled]);
  // Editor workspace slots. The lazily loaded workspace renders its header
  // controls, tab bar/rail and main surface into these elements, so the shell
  // keeps the exact layout without importing the editor.
  const [editorHeaderSlot, setEditorHeaderSlot] =
    React.useState<HTMLDivElement | null>(null);
  const [editorUtilitySlot, setEditorUtilitySlot] =
    React.useState<HTMLDivElement | null>(null);
  const [editorTopSlot, setEditorTopSlot] =
    React.useState<HTMLDivElement | null>(null);
  const [editorMainSlot, setEditorMainSlot] =
    React.useState<HTMLDivElement | null>(null);
  // Mount the workspace on the first visit to the simulation view and keep it
  // mounted afterwards, so background solves and editor state survive a trip
  // to the dashboard exactly as they did when the editor lived in App.
  const [editorWorkspaceMounted, setEditorWorkspaceMounted] = React.useState(
    viewMode !== "siterag",
  );
  if (viewMode !== "siterag" && !editorWorkspaceMounted) {
    setEditorWorkspaceMounted(true);
  }
  const [editorMainReady, setEditorMainReady] = React.useState(false);
  const editorMainVisible =
    !isIssuesListRoute &&
    !isIssueStatusRoute &&
    viewMode !== "siterag" &&
    simulationComputeEnabled;
  const [cloudDialogMounted, setCloudDialogMounted] =
    React.useState(isCloudDialogOpen);
  if (isCloudDialogOpen && !cloudDialogMounted) setCloudDialogMounted(true);
  const markEditorMainReady = React.useCallback(
    () => setEditorMainReady(true),
    [],
  );
  const simulationHeaderVisible = simulationComputeEnabled && viewMode === "simulation";
  const openDashboard = (): void => {
    setViewMode("siterag");
    setSiteRagView("dashboard");
    useStore.getState().setActiveRagReport(null);
    useStore.getState().setActiveSite(null);
    navigate("/");
  };
  const returnToSite = (): void => {
    const siteId = simulationSiteId(useStore.getState());
    if (siteId) {
      navigate(siteOverviewPath(siteId, "simulations"));
      setSiteRagView("site");
    } else {
      navigate("/sites");
      setSiteRagView("dashboard");
    }
    setViewMode("siterag");
  };

  return (
    <div
      // overflow-clip, not hidden: a hidden box can still be scrolled by focus or
      // scroll-into-view, which shifted the whole app sideways on narrow screens.
      className="fixed inset-0 flex h-[100dvh] w-full flex-col overflow-clip bg-background font-mono text-foreground"
      data-testid="application-viewport-shell"
    >
      {/* R-131-SUP-A — global impersonation banner (visible only while an admin
          is impersonating a user). Restored with the f7bb7a69 recovery. */}
      <ImpersonationBanner />
      {/* Top Navigation — Aurora shell rail (token-driven; theme-aware). */}
      {/* Simulation uses a compact workspace menu on narrow screens so File
          and account controls stay visible. Other views retain their rail scroll. */}
      <header className={`relative h-14 min-w-0 border-b border-border-subtle flex items-center justify-between bg-rail shrink-0 ${simulationHeaderVisible ? "gap-2 px-2 sm:px-4 min-[1400px]:gap-4" : "overflow-x-auto no-scrollbar gap-4 px-4"}`} data-testid="simulation-app-header">
        {/* Centered Datacrate wordmark — absolutely positioned so it stays
            visually centered regardless of the left (File/tabs) and right
            (notifications/user) group widths. pointer-events-none so it never
            intercepts clicks; hidden below lg and on the simulation workbench
            so the solver controls and result status remain readable.
            #110 — fade out while a top-bar toast/notification is showing
            (toastVisible, driven off the live sonner DOM) and restore when it
            clears. Kept mounted (opacity) rather than unmounted so it can never
            be permanently hidden. */}
        {/* Datacrate compact lockup (cube-with-accent + Site Intelligence + DATACRATE);
            co-branded as [Datacrate | customer logo] when the org has a custom logo. */}
        <div
          data-testid="app-header-wordmark"
          aria-hidden="true"
          className={`hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 items-center gap-3 pointer-events-none select-none transition-opacity duration-200 ${
            toastVisible ||
            viewMode === "simulation" ||
            (viewMode === "siterag" && siteRagView === "dashboard")
              ? "opacity-0"
              : "opacity-100"
          }`}
        >
          <CoBrandLockup
            variant="nav"
            customerLogoUrl={headerLogoIsCustom ? headerLogoUrl : null}
          />
        </div>
        {/* Keep both control groups at their intrinsic width. Shrinking this
            group allowed the editor toolbar to paint over File after publish
            status appeared; the header owns overflow on narrow viewports. */}
        <div className={`flex items-center min-w-0 ${simulationHeaderVisible ? "gap-1 sm:gap-2 min-[1400px]:overflow-x-auto" : "shrink-0 gap-2"}`}>
          {simulationComputeEnabled && viewMode === "simulation" && (
            <>
              <CoBrandLockup variant="nav" customerLogoUrl={headerLogoIsCustom ? headerLogoUrl : null} className="hidden min-[1400px]:flex" />
              <img src={headerLogoIsCustom ? headerLogoUrl : "/favicon.svg"} alt={headerLogoIsCustom ? "Organization logo" : "DataCrate"} className="hidden h-6 w-6 shrink-0 object-contain sm:max-[1400px]:block" />
              <span className="ml-2 hidden shrink-0 border-l border-border-subtle px-4 text-sm text-text-secondary min-[1400px]:inline">Simulation</span>
              <SimulationWorkspaceNavigation activeDomain="plant" onPlantClick={(event) => event.preventDefault()} />
              <div className="mx-2 hidden h-5 w-px shrink-0 bg-border-subtle min-[1400px]:block" aria-hidden />
            </>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="File"
                  className="h-8 gap-1.5 rounded-md border-transparent bg-transparent font-mono text-[10px] uppercase tracking-[0.08em] text-text-muted hover:border-border hover:bg-state-hover/40 hover:text-text-primary"
                >
                  <Menu className="w-3.5 h-3.5" />
                  <span className={simulationHeaderVisible ? "hidden sm:inline" : undefined}>File</span>
                </Button>
              }
            />
            <DropdownMenuContent
              align="start"
              className="w-56 rounded-md border-border bg-surface-1 shadow-card font-mono"
            >
              {simulationHeaderVisible && <>
                <DropdownMenuItem onClick={openDashboard} className="md:hidden">
                  <LayoutDashboard className="mr-2 h-4 w-4" aria-hidden />Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={returnToSite} className="md:hidden">
                  <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />Back to site
                </DropdownMenuItem>
              </>}
              <DropdownMenuItem
                onClick={() => {
                  setViewMode("siterag");
                  setSiteRagView("dashboard");
                  /* trigger new site wizard via custom event */ window.dispatchEvent(
                    new CustomEvent("datacrate:new-site"),
                  );
                }}
                className="rounded-none hover:bg-state-active focus:bg-state-active text-text-primary uppercase text-xs tracking-wider cursor-pointer"
              >
                <MapPin className="w-4 h-4 mr-2" />
                New Site
              </DropdownMenuItem>
              {siteRag.activeSiteId &&
                viewMode === "siterag" &&
                siteRagView === "site" && (
                  <DropdownMenuItem
                    onClick={() =>
                      window.dispatchEvent(
                        new Event("datacrate:toggle-site-lock"),
                      )
                    }
                    className="rounded-none hover:bg-state-active focus:bg-state-active text-text-primary uppercase text-xs tracking-wider cursor-pointer"
                    data-testid="file-menu-toggle-site-lock"
                  >
                    {activeSiteLocked ? (
                      <Unlock className="w-4 h-4 mr-2" />
                    ) : (
                      <Lock className="w-4 h-4 mr-2" />
                    )}
                    {activeSiteLocked ? "Unlock Site" : "Lock Site"}
                  </DropdownMenuItem>
                )}
              {siteRag.activeSiteId &&
                viewMode === "siterag" &&
                siteRagView === "site" && (
                  <DropdownMenuItem
                    onClick={() =>
                      window.dispatchEvent(new Event("datacrate:delete-site"))
                    }
                    className="rounded-none hover:bg-danger/10 focus:bg-danger/10 text-danger uppercase text-xs tracking-wider cursor-pointer"
                    data-testid="file-menu-delete-site"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Site
                  </DropdownMenuItem>
                )}
              {/* Canvas/editor-only actions: New Simulation, Save to Cloud, and
                  Load Model all operate on the active simulation canvas, so they
                  only appear in the Simulation section. Hidden (with their
                  separators) on the dashboard/site views to avoid meaningless
                  entries. */}
              {simulationComputeEnabled && viewMode === "simulation" && (
                <ChunkLoadBoundary name="EditorFileMenuItems">
                  <React.Suspense fallback={null}>
                    <EditorFileMenuItems />
                  </React.Suspense>
                </ChunkLoadBoundary>
              )}

              <DropdownMenuSeparator className="bg-state-active" />

              {/* Per-company branding (company name / brand colour /
                  logo). The report DESIGN is locked to the golden stock
                  template; only branding remains user-editable, here. */}
              <DropdownMenuItem
                onClick={() => navigate("/settings/branding")}
                data-testid="file-menu-brand-settings"
                className="rounded-none hover:bg-state-active focus:bg-state-active text-text-primary uppercase text-xs tracking-wider cursor-pointer"
              >
                <Palette className="w-4 h-4 mr-2" />
                Brand Settings
              </DropdownMenuItem>
              {companyId && canEditSuppliers && (
                <DropdownMenuItem
                  onClick={() => navigate("/company/suppliers")}
                  data-testid="file-menu-company-suppliers"
                  className="rounded-none hover:bg-state-active focus:bg-state-active text-text-primary uppercase text-xs tracking-wider cursor-pointer"
                >
                  <Truck className="w-4 h-4 mr-2" />
                  Company Suppliers
                </DropdownMenuItem>
              )}
              {companyId &&
                siteRag.sites.filter(
                  (s) => !s.companyId || s.companyId === companyId,
                ).length >= 2 && (
                  <DropdownMenuItem
                    onClick={() =>
                      navigate(`/customers/${companyId}/portfolio`)
                    }
                    data-testid="file-menu-portfolio"
                    className="rounded-none hover:bg-state-active focus:bg-state-active text-text-primary uppercase text-xs tracking-wider cursor-pointer"
                  >
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Portfolio
                  </DropdownMenuItem>
                )}
              {userRole === "admin" && (
                <>
                  <DropdownMenuSeparator className="bg-state-active" />
                  <DropdownMenuItem
                    onClick={() => navigate("/admin")}
                    className="rounded-none hover:bg-state-active focus:bg-state-active text-text-primary uppercase text-xs tracking-wider cursor-pointer"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Admin Panel
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <div
            className="hidden md:block h-5 w-px shrink-0 bg-border-subtle"
            aria-hidden
          />

          {/* Site RAG view switcher — compact segmented control (dark-mode friendly) */}
          <nav
            className={`${simulationHeaderVisible ? "hidden md:inline-flex" : "inline-flex"} min-w-0 shrink-0 items-center gap-0.5 overflow-x-auto rounded-md border border-border-subtle bg-inset p-0.5 no-scrollbar`}
            role="tablist"
            aria-label="Site RAG views"
          >
            <button
              type="button"
              role="tab"
              aria-selected={
                siteRagView === "dashboard" && viewMode === "siterag"
              }
              className={siteragNavSegmentClass(
                siteRagView === "dashboard" && viewMode === "siterag",
              )}
              onClick={openDashboard}
            >
              <LayoutDashboard className="w-3.5 h-3.5 shrink-0" aria-hidden />
              Dashboard
            </button>
            {/* KEEP-SITE-TAB-IN-REPORT-VIEW — the `siteRagView !== 'report'`
                guard used to hide the Site tab while viewing a report, stranding
                the user in the report with no tab back to the site (the report
                view replaces SiteLandingPage's own sub-tab row). Show it always
                (when a site is active) so the user can always navigate back to
                the site from a report. */}
            {siteRag.activeSiteId && viewMode === "siterag" && (
              <button
                type="button"
                role="tab"
                aria-selected={siteRagView === "site"}
                className={siteragNavSegmentClass(siteRagView === "site")}
                onClick={() => {
                  const siteId = siteRag.activeSiteId;
                  if (!siteId) return;
                  setSiteRagView("site");
                  navigate(siteOverviewPath(siteId));
                }}
              >
                <MapPin className="w-3.5 h-3.5 shrink-0" aria-hidden />
                Site
              </button>
            )}
          </nav>
          {/* BACK TO SITE — the simulation editor leaves the site shell (the
              Site segment above is hidden in simulation mode). When a site is
              active, return to its Engineering Analysis tab, where the
              excursion started. (Path to GO removal, stage 2: this was the
              retired guided loop's Finish button.) The C7 URL-sync effects
              settle viewMode for /sites/... on their own. Navigate before
              changing state so the old /editor path cannot reclaim the view. */}
          {simulationComputeEnabled &&
            viewMode === "simulation" && (
              <Button
                size="sm"
                variant="outline"
                className="ml-1 hidden shrink-0 md:inline-flex"
                data-testid="sim-back-to-site"
                onClick={returnToSite}
              >
                <ArrowLeft className="w-3.5 h-3.5 shrink-0" aria-hidden />
                Back to site
              </Button>
            )}
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2">
          {cloudDialogMounted && (
            <ChunkLoadBoundary name="SaveLoadDialog">
              <React.Suspense fallback={null}>
                <SaveLoadDialog
                  open={isCloudDialogOpen}
                  onOpenChange={setIsCloudDialogOpen}
                />
              </React.Suspense>
            </ChunkLoadBoundary>
          )}
          <UserApiKeysSettings
            open={isApiKeysSettingsOpen}
            onClose={() => setIsApiKeysSettingsOpen(false)}
          />
          <LandGateConnectionDialog
            open={isLandGateConnectionOpen}
            onOpenChange={setIsLandGateConnectionOpen}
          />
          <UploadQueuePanel
            open={isUploadQueueOpen}
            onClose={() => setIsUploadQueueOpen(false)}
          />
          {isAdminPanelOpen ? (
            <React.Suspense fallback={null}>
              <AdminPanel
                open={isAdminPanelOpen}
                // Closing the admin panel = leaving the /admin/* route. Navigate
                // back to the root; the rest of the app's state-based navigation
                // (siteRagView / viewMode / etc.) is unchanged and resumes from
                // wherever the user left it.
                onClose={() => navigate("/")}
                initialTab={adminPanelInitialTab}
              />
            </React.Suspense>
          ) : null}

          <div ref={setEditorHeaderSlot} className="contents" data-editor-slot="header" />

          <ThemeToggle />
          <NotificationBell />
          <UserMenu />
        </div>
      </header>

      {/* Tab Bar + workflow rail — rendered by the editor workspace, hidden in SiteRAG mode */}
      <div ref={setEditorTopSlot} className="contents" data-editor-slot="top" />

      {/* Main Content */}
      <div className="flex min-h-0 w-full min-w-0 flex-1 overflow-hidden">
        {isIssuesListRoute ? (
          <React.Suspense fallback={<RouteLoadingSkeleton />}>
            <MyIssuesPage />
          </React.Suspense>
        ) : isIssueStatusRoute ? (
          <React.Suspense fallback={<RouteLoadingSkeleton />}>
            <IssueStatusPage />
          </React.Suspense>
        ) : viewMode === "siterag" || !simulationComputeEnabled ? (
          <SiteRagEngine
            engineView={siteRagView}
            setEngineView={setSiteRagView}
            useDashboardV2={useDashboardV2}
          />
        ) : null}
        {/* Editor main surface slot. It stays mounted (hidden outside the
            editor) so its ref fires once: a slot that mounted and unmounted
            with every view flip turned the view-sync effects' brief
            simulation/siterag exchange during navigation into a synchronous
            update loop. The workspace renders into it only while visible. */}
        <div
          ref={setEditorMainSlot}
          className={editorMainVisible ? "contents" : "hidden"}
          data-editor-slot="main"
        >
          {editorMainVisible && !editorMainReady && <RouteLoadingSkeleton />}
        </div>
      </div>
      {editorWorkspaceMounted && (
        <ChunkLoadBoundary name="EditorWorkspace">
          <React.Suspense fallback={null}>
            <EditorWorkspace
              headerSlot={editorHeaderSlot}
              topSlot={editorTopSlot}
              mainSlot={editorMainSlot}
              utilitySlotRef={setEditorUtilitySlot}
              isCloudDialogOpen={isCloudDialogOpen}
              setIsCloudDialogOpen={setIsCloudDialogOpen}
              onMainMounted={markEditorMainReady}
            />
          </React.Suspense>
        </ChunkLoadBoundary>
      )}
      <ChunkLoadBoundary name="AutoSaver">
        <React.Suspense fallback={null}>
          <AutoSaver />
        </React.Suspense>
      </ChunkLoadBoundary>
      <DmsMetricsAutoFlusher />
      <FeedbackButton launcherSlot={editorMainVisible ? editorUtilitySlot : null} />
      {/* #81(c) first-login welcome overlay removed 2026-06-10 per William —
          the guide popup no longer interrupts first login. The component +
          its localStorage helpers are retained (unmounted) so it can be
          re-enabled by restoring this mount if ever wanted. */}
      <Toaster
        position="bottom-right"
        theme="dark"
        toastOptions={{
          className:
            "rounded-none border-border bg-background font-mono text-[10px] uppercase tracking-wider",
        }}
      />
    </div>
  );
}
